import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Icon from "components/AppIcon";
import toast from "react-hot-toast";
import {
  viewPurchaseOrderPdf,
  downloadPurchaseOrderPdf,
} from "api/purchaseOrder.api";

const STATUS_CONFIG = {
  draft: {
    label: "Draft",
    color: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400",
  },
  pendingApproval: {
    label: "Pending Approval",
    color:
      "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
  },
  approved: {
    label: "Approved",
    color: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
  },
  convertedToBill: {
    label: "Converted to Bill",
    color:
      "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300",
  },
  cancelled: {
    label: "Cancelled",
    color: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
  },
};

const AVATAR_COLORS = [
  "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  "bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300",
  "bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300",
  "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300",
  "bg-pink-100 text-pink-700 dark:bg-pink-900/40 dark:text-pink-300",
];

const PurchaseOrderTable = ({
  purchaseOrders,
  onEdit,
  onSubmit,
  onApprove,
  onConvert,
  onCancel,
  onDelete,
  busyId,
}) => {
  if (!purchaseOrders.length) return null;

  return (
    <div className="bg-card rounded-xl shadow-sm border border-border overflow-visible">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted">
            <tr>
              {[
                "Date",
                "PO #",
                "Vendor",
                "Contact",
                "Email",
                "Amount (SR)",
                "Status",
                "Actions",
              ].map((h) => (
                <th
                  key={h}
                  className="px-5 py-4 text-left text-sm font-medium text-foreground whitespace-nowrap"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {purchaseOrders.map((po, index) => (
              <PORow
                key={po.id}
                po={po}
                index={index}
                busy={busyId === po.id}
                onEdit={onEdit}
                onSubmit={onSubmit}
                onApprove={onApprove}
                onConvert={onConvert}
                onCancel={onCancel}
                onDelete={onDelete}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const PORow = ({
  po,
  index,
  busy,
  onEdit,
  onSubmit,
  onApprove,
  onConvert,
  onCancel,
  onDelete,
}) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const statusConfig = STATUS_CONFIG[po.status] || STATUS_CONFIG.draft;

  const initials = (po.vendorName || "?")
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
  const avatarColor = AVATAR_COLORS[index % AVATAR_COLORS.length];

  // Actions available depend on the PO's status.
  const actions = [
    {
      icon: "Eye",
      label: "View PDF",
      color: "text-violet-500 hover:bg-violet-50 dark:hover:bg-violet-900/20",
      onClick: async () => {
        try {
          await viewPurchaseOrderPdf(po.id);
        } catch (e) {
          toast.error(e?.message || "Could not open PDF");
        }
      },
    },
    {
      icon: "Download",
      label: "Download PDF",
      color: "text-teal-500 hover:bg-teal-50 dark:hover:bg-teal-900/20",
      onClick: async () => {
        try {
          await downloadPurchaseOrderPdf(po.id, `${po.poNumber}.pdf`);
        } catch (e) {
          toast.error(e?.message || "Download failed");
        }
      },
    },
  ];

  if (["draft", "pendingApproval"].includes(po.status)) {
    actions.push({
      icon: "Edit3",
      label: "Edit PO",
      color: "text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20",
      onClick: () => onEdit(po),
    });
  }
  if (po.status === "draft") {
    actions.push({
      icon: "Send",
      label: "Submit for Approval",
      color: "text-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-900/20",
      onClick: () => onSubmit(po),
    });
  }
  if (po.status === "pendingApproval") {
    actions.push({
      icon: "CheckCircle2",
      label: "Approve",
      color: "text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20",
      onClick: () => onApprove(po),
    });
  }
  if (po.status === "approved") {
    actions.push({
      icon: "ReceiptText",
      label: "Convert to Bill",
      color: "text-orange-500 hover:bg-orange-50 dark:hover:bg-orange-900/20",
      onClick: () => onConvert(po),
    });
  }
  if (["draft", "pendingApproval", "approved"].includes(po.status)) {
    actions.push({
      icon: "Ban",
      label: "Cancel PO",
      color: "text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20",
      onClick: () => onCancel(po),
    });
  }
  if (["draft", "pendingApproval", "cancelled"].includes(po.status)) {
    actions.push({
      icon: "Trash2",
      label: "Delete",
      color: "text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20",
      onClick: () => setShowDeleteConfirm(true),
    });
  }

  return (
    <>
      <motion.tr
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25, delay: index * 0.04 }}
        className="hover:bg-muted/50 transition-colors"
      >
        <td className="px-5 py-4 text-sm text-foreground whitespace-nowrap">
          {po.date
            ? new Date(po.date).toLocaleDateString("en-SA", {
                year: "numeric",
                month: "short",
                day: "numeric",
              })
            : "—"}
        </td>
        <td className="px-5 py-4">
          <span className="font-mono text-sm text-primary font-semibold">
            {po.poNumber || "—"}
          </span>
        </td>
        <td className="px-5 py-4">
          <div className="flex items-center gap-2.5">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0 ${avatarColor}`}
            >
              {initials}
            </div>
            <span className="text-sm font-medium text-foreground">
              {po.vendorName || "—"}
            </span>
          </div>
        </td>
        <td className="px-5 py-4 text-sm text-muted-foreground">
          {po.contact || "—"}
        </td>
        <td className="px-5 py-4 text-sm text-muted-foreground">
          {po.email || "—"}
        </td>
        <td className="px-5 py-4">
          <span className="font-semibold text-foreground">
            {parseFloat(po.netAmount || 0).toLocaleString("en-SA", {
              minimumFractionDigits: 2,
            })}
          </span>
        </td>
        <td className="px-5 py-4">
          <span
            className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${statusConfig.color}`}
          >
            {statusConfig.label}
          </span>
        </td>
        <td className="px-5 py-4">
          <div className="flex items-center gap-1">
            {busy ? (
              <Icon
                name="Loader2"
                size={16}
                className="animate-spin text-muted-foreground"
              />
            ) : actions.length ? (
              actions.map((a) => <ActionBtn key={a.label} {...a} />)
            ) : (
              <span className="text-xs text-muted-foreground">—</span>
            )}
          </div>
        </td>
      </motion.tr>

      <AnimatePresence>
        {showDeleteConfirm && (
          <motion.tr
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <td
              colSpan={8}
              className="px-5 py-3 bg-red-50 dark:bg-red-900/10 border-b border-red-200 dark:border-red-800"
            >
              <div className="flex items-center justify-between">
                <span className="text-sm text-red-700 dark:text-red-300 font-medium">
                  Delete PO <strong>{po.poNumber}</strong> for{" "}
                  <strong>{po.vendorName}</strong>? This cannot be undone.
                </span>
                <div className="flex gap-2 ml-4 flex-shrink-0">
                  <button
                    onClick={() => setShowDeleteConfirm(false)}
                    className="px-3 py-1.5 text-sm rounded-lg border border-border text-foreground hover:bg-muted transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      onDelete(po.id);
                      setShowDeleteConfirm(false);
                    }}
                    className="px-3 py-1.5 text-sm rounded-lg bg-red-500 text-white hover:bg-red-600 transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </td>
          </motion.tr>
        )}
      </AnimatePresence>
    </>
  );
};

const ActionBtn = ({ icon, label, color, onClick }) => (
  <div className="relative group">
    <button
      onClick={onClick}
      className={`p-1.5 rounded-lg transition-colors ${color}`}
    >
      <Icon name={icon} size={14} />
    </button>
    <div className="absolute bottom-full mb-1.5 left-1/2 -translate-x-1/2 px-2 py-1 bg-popover text-popover-foreground text-xs rounded-md shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50 border border-border">
      {label}
    </div>
  </div>
);

export default PurchaseOrderTable;
